import React from 'react';

/**
 * Render chord-chart style lyrics. Input format:
 *
 *   [Am]There is a [C]house in [D]New Or-[F]leans
 *
 * For each line we emit two stacked monospace rows so chords sit
 * exactly above their syllables:
 *
 *       Am         C        D      F
 *   There is a house in  New Or-leans
 *
 * Lines without any [chord] (descriptive blurbs in original training
 * tracks) are rendered as italic prose.
 */

const CHORD_TAG = /\[([^\]]+)\]/g;

const parseLine = (line) => {
  const segments = [];
  let lastIndex = 0;
  let pendingChord = '';
  CHORD_TAG.lastIndex = 0;
  let match;

  while ((match = CHORD_TAG.exec(line)) !== null) {
    const text = line.slice(lastIndex, match.index);
    if (text.length > 0 || pendingChord) {
      segments.push({ chord: pendingChord, text: text });
    }
    pendingChord = match[1];
    lastIndex = CHORD_TAG.lastIndex;
  }

  const tail = line.slice(lastIndex);
  if (pendingChord || tail.length > 0) {
    segments.push({ chord: pendingChord, text: tail });
  }

  return segments;
};

const padEnd = (s, n) => {
  if (s.length >= n) return s;
  return s + ' '.repeat(n - s.length);
};

const buildRows = (segments) => {
  let chordRow = '';
  let lyricRow = '';
  for (const seg of segments) {
    const chord = seg.chord || '';
    const text = seg.text || '';
    // 1-char gap between segments so adjacent chord names don't kiss
    const width = Math.max(chord.length, text.length) + 1;
    chordRow += padEnd(chord, width);
    lyricRow += padEnd(text, width);
  }
  return { chordRow: chordRow.replace(/\s+$/, ''), lyricRow: lyricRow.replace(/\s+$/, '') };
};

const LyricsWithChords = ({ children, className = '' }) => {
  if (!children) return null;
  const raw = String(children).replace(/\r\n/g, '\n');
  const lines = raw.split('\n');

  return (
    <div className={className}>
      {lines.map((line, idx) => {
        // Empty line → vertical spacer
        if (line.trim() === '') return <div key={idx} className="h-3" />;

        // No [chord] → render as italic prose
        if (!/\[[^\]]+\]/.test(line)) {
          return (
            <p
              key={idx}
              className="text-zinc-400 text-[15px] leading-relaxed my-2"
            >
              {line}
            </p>
          );
        }

        const segments = parseLine(line);
        const { chordRow, lyricRow } = buildRows(segments);
        return (
          <div key={idx} className="font-mono text-sm whitespace-pre overflow-x-auto leading-tight mb-3">
            <div className="text-amber font-bold">{chordRow || ' '}</div>
            <div className="text-zinc-100">{lyricRow || ' '}</div>
          </div>
        );
      })}
    </div>
  );
};

export default LyricsWithChords;
